import { StatTile } from "@components/ui/StatTile"
import { useGradeScale } from "@hooks/useGradeScale"
import { formatGrade, formatPercent } from "@lib/intl"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { gradeTier, gradeTierTextClass, type GradeTierMode } from "@/constants/gradeTier"
import type { GradeScale, HomeKpis as HomeKpisShape } from "@/types"

interface Props {
  kpis: HomeKpisShape
  onKpiClick?: (kpi: "total_students" | "avg_grade" | "delivery_rate" | "at_risk") => void
}

// Portfolio-level average → "group" tier (stricter than the per-student
// passing-mark thresholds). Reads the active scale so a 0-100 professor sees
// 85/75 breakpoints instead of 8.5/7.5.
const gradeColor = (avg: number, scale: GradeScale, mode: GradeTierMode = "group") =>
  gradeTierTextClass(gradeTier(avg, scale, mode))

// Delivery rate is a 0-100 percentage regardless of grade scale.
const deliveryColor = (rate: number) => gradeTierTextClass(gradeTier(rate, "0-100", "group"))

/**
 * The semester view (PRD-59 §FR-7). Four cumulative figures, now BELOW the
 * day's work rather than above it: the largest number on the page used to be
 * a portfolio average that moves 0.1 a month.
 *
 * Three changes beyond the move:
 *   • `StatTile` replaces a fifth hand-rolled KPI card, so the strip matches
 *     the classroom, student and material strips.
 *   • The subtitle was the ONLY explanation of what each figure means and it
 *     lived in a `title` attribute — unreachable on the phone this product is
 *     used on. It is visible text now.
 *   • The at-risk tile stopped printing its own number twice ("7" over
 *     "7 alumnos") and stopped linking to `/salones`, a grid that contains no
 *     student names; the list of those students is on this page already.
 */
export const HomeKpis = memo(function HomeKpis({ kpis, onKpiClick }: Props) {
  const { t } = useTranslation("home")
  const navigate = useNavigate()
  const scale = useGradeScale()
  const hasGrades = kpis.avg_grade > 0
  const dash = t("dash")

  return (
    <section aria-labelledby="home-kpis-title" className="space-y-2">
      <h2 id="home-kpis-title" className="text-label uppercase">
        {t("kpis.section_title")}
      </h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label={t("kpis.total_students.label")}
          value={kpis.total_students}
          hint={t("kpis.total_students.subtitle", { count: kpis.total_classrooms })}
          onClick={
            kpis.total_classrooms > 0
              ? () => {
                  onKpiClick?.("total_students")
                  void navigate("/salones")
                }
              : undefined
          }
        />
        <StatTile
          label={t("kpis.avg_grade.label")}
          value={hasGrades ? formatGrade(kpis.avg_grade, scale) : dash}
          valueClassName={gradeColor(kpis.avg_grade, scale)}
          hint={hasGrades ? t("kpis.avg_grade.subtitle") : t("kpis.avg_grade.subtitle_empty")}
        />
        <StatTile
          label={t("kpis.delivery_rate.label")}
          value={kpis.total_records > 0 ? formatPercent(kpis.delivery_rate) : dash}
          valueClassName={deliveryColor(kpis.delivery_rate)}
          hint={
            kpis.total_records > 0
              ? t("kpis.delivery_rate.subtitle", {
                  delivered: Math.round((kpis.delivery_rate * kpis.total_records) / 100),
                  total: kpis.total_records,
                })
              : t("kpis.delivery_rate.subtitle_empty")
          }
        />
        <StatTile
          label={t("kpis.at_risk.label")}
          value={kpis.at_risk_count}
          tone={kpis.at_risk_count > 0 ? "red" : "neutral"}
          hint={
            kpis.at_risk_count > 0
              ? t("kpis.at_risk.subtitle_named")
              : t("kpis.at_risk.subtitle_clear")
          }
        />
      </div>
    </section>
  )
})
